import { MarginType } from '@katanaperps/katana-perps-sdk';

import { ApiOrder, ApiPosition } from '#/api/types';
import { MarketSessionInfo } from '#/components/markets/intermittent/intermittentMarkets';
import {
  getIsolatedMarketRestrictionTooltip,
  getOpenIsolatedMarketRestrictionTooltip,
} from '#/components/markets/intermittent/intermittentMarketsCopy';

/**
 * Markets in which the wallet currently has an open order or position.
 * Undefined while orders/positions are not loaded (wallet not connected or loading).
 */
export function getWalletOpenMarkets(
  orders: ApiOrder[] | undefined,
  positions: ApiPosition[] | undefined
): Set<string> | undefined {
  if (!orders && !positions) {
    return undefined;
  }
  const openMarkets = new Set<string>();
  orders?.forEach(order => openMarkets.add(order.market));
  positions?.forEach(position => {
    if (Number(position.quantity)) {
      openMarkets.add(position.market);
    }
  });
  return openMarkets;
}

/**
 * Isolated margin trading rules (hover tip when Buy/Sell is blocked, undefined when allowed):
 * - a wallet open in any other market cannot trade an isolated margin market
 * - a wallet open in an isolated margin market cannot trade any other market
 */
export function getIsolatedMarginRestriction({
  market,
  marginType,
  openMarkets,
  marketsTradingInfo,
}: {
  market: string | undefined;
  marginType: MarginType | undefined;
  openMarkets: Set<string> | undefined;
  marketsTradingInfo: { [market: string]: MarketSessionInfo } | undefined;
}): string | undefined {
  if (!market || !openMarkets) {
    return undefined;
  }
  const otherOpenMarkets = [...openMarkets].filter(
    openMarket => openMarket !== market
  );
  if (otherOpenMarkets.length === 0) {
    return undefined;
  }
  if (marginType === 'isolated') {
    return getIsolatedMarketRestrictionTooltip(market);
  }
  const openIsolatedMarket = otherOpenMarkets.find(
    openMarket => marketsTradingInfo?.[openMarket]?.marginType === 'isolated'
  );
  if (openIsolatedMarket) {
    return getOpenIsolatedMarketRestrictionTooltip(openIsolatedMarket);
  }
  return undefined;
}
